import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

interface User {
  id: string;
  name: string;
  email: string;
  username: string;
  role: string;
}

interface OfflinePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const OfflinePaymentModal: React.FC<OfflinePaymentModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState({
    targetUserId: '',
    amount: '',
    description: '',
    paymentMethod: 'CASH',
    referenceNumber: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paymentMethods = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHECK', label: 'Check' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'MONEY_ORDER', label: 'Money Order' },
    { value: 'CASHIERS_CHECK', label: "Cashier's Check" },
    { value: 'OTHER', label: 'Other' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
      if (!selectedGroupId) {
        setError('No property group selected');
        return;
      }

      const response = await axios.get<User[]>('/api/users', {
        headers: { 
          Authorization: `Bearer ${user?.token}`,
          'x-selected-group': selectedGroupId
        }
      });
      // Filter to show residents and vendors (not other admins)
      const filteredUsers = response.data.filter(u => u.role === 'RESIDENT' || u.role === 'VENDOR');
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
      await axios.post('/api/payments/offline', formData, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          ...(selectedGroupId ? { 'x-selected-group': selectedGroupId } : {})
        }
      });

      // Reset form
      setFormData({
        targetUserId: '',
        amount: '',
        description: '',
        paymentMethod: 'CASH',
        referenceNumber: '',
        notes: ''
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error recording offline payment:', error);
      setError(error.response?.data?.message || 'Failed to record payment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="floating-card w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Record Offline Payment</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Selection */}
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Select User *
            </label>
            <select
              name="targetUserId"
              value={formData.targetUserId}
              onChange={handleInputChange}
              required
              className="input-field"
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email}) - {user.role}
                </option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Payment Amount *
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              required
              className="input-field"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Payment Description *
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Monthly Rent, HOA Fee, Special Assessment, etc."
              required
              className="input-field"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Payment Method *
            </label>
            <select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              required
              className="input-field"
            >
              {paymentMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Reference Number */}
          {(formData.paymentMethod === 'CHECK' || formData.paymentMethod === 'BANK_TRANSFER' || 
            formData.paymentMethod === 'MONEY_ORDER' || formData.paymentMethod === 'CASHIERS_CHECK') && (
            <div>
              <label className="block text-white/60 text-sm font-medium mb-2">
                Reference Number
                {formData.paymentMethod === 'CHECK' && ' (Check #)'}
                {formData.paymentMethod === 'BANK_TRANSFER' && ' (Confirmation #)'}
                {(formData.paymentMethod === 'MONEY_ORDER' || formData.paymentMethod === 'CASHIERS_CHECK') && ' (Serial #)'}
              </label>
              <input
                type="text"
                name="referenceNumber"
                value={formData.referenceNumber}
                onChange={handleInputChange}
                placeholder={
                  formData.paymentMethod === 'CHECK' ? 'Check number' :
                  formData.paymentMethod === 'BANK_TRANSFER' ? 'Confirmation number' :
                  'Reference number'
                }
                className="input-field"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Additional Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Any additional details about this payment..."
              rows={3}
              className="input-field resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary"
              disabled={isLoading || !formData.targetUserId || !formData.amount || !formData.description}
            >
              {isLoading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 rounded-lg bg-blue-500/20 border border-blue-500/30">
          <div className="flex items-start space-x-2">
            <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-300">
              <p className="font-medium mb-1">Recording Offline Payments</p>
              <p className="text-blue-300/80">
                This will create a payment record marked as "PAID" for the selected user. 
                The payment will appear in their payment history immediately.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflinePaymentModal; 